import {
  Radio,
  Users,
  UtensilsCrossed,
  ShoppingBasket,
  FileText,
  Boxes,
  MonitorSmartphone,
  CreditCard,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import Reveal from '@/components/Reveal';
import adminCashier from '@/images/Cashier.webp';
import adminHome from '@/images/Menu.webp';
import analyticsChart from '@/images/screens/admin-analytics-chart.webp';
import shiftReport from '@/images/screens/admin-shift-report.webp';

interface Capability {
  icon: LucideIcon;
  title: string;
  text: string;
}

const REST: Capability[] = [
  { icon: Radio, title: 'Текущая касса', text: 'Открытая смена в реальном времени: заказы, оплаты, средний чек.' },
  { icon: Users, title: 'Пользователи', text: 'Сотрудники и роли: владелец, администратор, кассир, у каждого свои права.' },
  { icon: UtensilsCrossed, title: 'Меню', text: 'Категории, блюда, модификаторы и стоп-лист. Меняется на лету.' },
  { icon: ShoppingBasket, title: 'Заказы', text: 'Каждый заказ с позициями, оплатой и официантом. Полная история.' },
  { icon: Boxes, title: 'Склад', text: 'Остатки, тех-карты, списания и себестоимость блюд.' },
  { icon: FileText, title: 'Отчёты', text: 'Выгрузка по выручке, сменам и позициям в Excel и PDF.' },
  { icon: MonitorSmartphone, title: 'Устройства', text: 'Кассы на iPad и iPhone, сетевые принтеры чеков.' },
  { icon: CreditCard, title: 'Подписка и оплата', text: 'Тариф, счета и автосписание прозрачно. Отмена в один клик.' },
  { icon: Settings, title: 'Настройки', text: 'Реквизиты, налоги, печать чеков и параметры заведения.' },
];

const ANALYTICS_FACTS: Array<[string, string]> = [
  ['периоды', 'Сегодня · 7 · 30 · 90 дней'],
  ['динамика', 'К прошлому периоду'],
  ['выгрузка', 'Excel и PDF'],
];

export default function Features() {
  return (
    <section id="features" className="section section-divider scroll-mt-24 bg-white">
      <div className="container-site">
        <div className="grid gap-10 md:grid-cols-12 md:items-end">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="h2">Всё управление заведением в&nbsp;одном месте</h2>
            </Reveal>
          </div>
          <Reveal delay={80} className="md:col-span-5">
            <p className="lead">
              Веб-админка работает в&nbsp;браузере и&nbsp;на&nbsp;десктопе, без локального сервера.
              Двенадцать разделов, каждый готовый инструмент, ничего не&nbsp;нужно докупать.
            </p>
          </Reveal>
        </div>

        {/* Плитки: четыре реальных экрана админки. */}
        <Reveal delay={120}>
          <div className="mt-14 grid gap-4 sm:mt-16 md:grid-cols-12">
            <article className="card overflow-hidden md:col-span-8">
              <div className="flex flex-col gap-3 p-7 sm:flex-row sm:items-end sm:justify-between sm:p-8">
                <div>
                  <h3 className="text-[20px] font-medium tracking-tight text-ink">Главная</h3>
                  <p className="mt-2 max-w-md text-[14.5px] leading-relaxed text-ink/60">
                    Выручка, заказы и активные смены всех точек на одном экране, обновляется само.
                  </p>
                </div>
                <span className="shrink-0 text-[12px] font-medium text-ink/45">admin.resstro.ru</span>
              </div>
              <div className="px-5 sm:px-8">
                <div className="overflow-hidden rounded-t-2xl border border-b-0 border-line bg-white shadow-lift">
                  <img
                    src={adminHome}
                    alt="Веб-админка Resstro, главная: выручка, заказы и топ блюд за сегодня"
                    className="block w-full"
                    width={1800}
                    height={1048}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </article>

            <article className="card flex flex-col bg-paper p-7 sm:p-8 md:col-span-4">
              <div>
                <h3 className="text-[20px] font-medium tracking-tight text-ink">Аналитика</h3>
                <p className="mt-2 text-[14.5px] leading-relaxed text-ink/60">
                  Выручка по дням, блюда-лидеры, источники заказов и динамика к прошлому периоду. Без Excel.
                </p>
              </div>
              <div className="mt-8 overflow-hidden rounded-xl bg-white shadow-lift ring-1 ring-ink/10">
                <img
                  src={analyticsChart}
                  alt="Раздел «Аналитика» в админке Resstro: оборот по дням и источники заказов"
                  className="block w-full"
                  width={1181}
                  height={353}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <dl className="mt-auto divide-y divide-line border-t border-line pt-0 text-[13px]">
                {ANALYTICS_FACTS.map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between py-2.5">
                    <dt className="text-ink/50">{k}</dt>
                    <dd className="font-medium text-ink">{v}</dd>
                  </div>
                ))}
              </dl>
            </article>

            <article className="card flex flex-col overflow-hidden p-7 sm:p-8 md:col-span-4">
              <div>
                <h3 className="text-[20px] font-medium tracking-tight text-ink">Смены и Z-отчёты</h3>
                <p className="mt-2 text-[14.5px] leading-relaxed text-ink/60">
                  Открытие и закрытие смены, отчёт в PDF или на печать, сверка наличных и расхождения по кассе.
                </p>
              </div>
              <div className="-mb-7 mt-8 overflow-hidden rounded-t-2xl border border-b-0 border-line bg-white shadow-lift sm:-mb-8">
                <img
                  src={shiftReport}
                  alt="Отчёт по смене в админке Resstro: выручка, средний чек, сверка наличных"
                  className="block w-full"
                  width={570}
                  height={560}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </article>

            <article className="card overflow-hidden md:col-span-8">
              <div className="flex flex-col gap-3 p-7 sm:flex-row sm:items-end sm:justify-between sm:p-8">
                <div>
                  <h3 className="text-[20px] font-medium tracking-tight text-ink">Текущая касса</h3>
                  <p className="mt-2 max-w-md text-[14.5px] leading-relaxed text-ink/60">
                    Открытая смена в реальном времени: заказы, оплаты, средний чек и источники, касса против
                    приложения.
                  </p>
                </div>
                <span className="shrink-0 text-[12px] font-medium text-ink/45">Обновление каждые 5 секунд</span>
              </div>
              <div className="px-5 sm:px-8">
                <div className="overflow-hidden rounded-t-2xl border border-b-0 border-line bg-white shadow-lift">
                  <img
                    src={adminCashier}
                    alt="Веб-админка Resstro, текущая касса: выручка смены, активные заказы, оплаты по способам"
                    className="block w-full"
                    width={1800}
                    height={1049}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </article>
          </div>
        </Reveal>

        {/* Остальные разделы плотным списком. */}
        <Reveal delay={160}>
          <ul className="mt-4 grid grid-cols-1 gap-x-8 gap-y-0.5 rounded-3xl border border-line bg-canvas p-3 sm:grid-cols-3">
            {REST.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex items-start gap-3.5 rounded-2xl p-5">
                <Icon size={17} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink/35" />
                <div>
                  <h3 className="text-[14.5px] font-medium tracking-tight text-ink">{title}</h3>
                  <p className="mt-1 text-[13px] leading-relaxed text-ink/55">{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}
